import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { TimeSuggestion } from '../types'

/**
 * Props for the useComboboxAccessibility hook
 */
interface UseComboboxAccessibilityProps {
  /** Whether the suggestion dropdown is currently open */
  isOpen: boolean
  /** The currently highlighted suggestion index (-1 if none) */
  activeIndex: number
  /** Array of available time suggestions */
  suggestions: TimeSuggestion[]
  /** Total count of suggestions available */
  suggestionCount: number
  /** Whether the input has a validation error */
  hasError: boolean
  /** ID of the error message element for aria-errormessage */
  errorId: string | undefined
  /** Optional ID for the input element */
  id?: string
  /** Optional name for the input element */
  name?: string
  /** Optional accessible label for the input */
  ariaLabel?: string
  /** Optional IDs of elements that describe the input */
  ariaDescribedBy?: string
}

/**
 * Return type for the useComboboxAccessibility hook
 */
interface ComboboxAccessibilityResult {
  /** Unique ID for the suggestion listbox element */
  listboxId: string
  /** Dynamic message for screen reader announcements */
  liveRegionMessage: string
  /** ARIA attributes to spread onto the input element */
  inputAriaProps: {
    /** Indicates if the input has a validation error */
    'aria-invalid': true | undefined
    /** ID of the error message element */
    'aria-errormessage': string | undefined
    /** ID of the listbox that this input controls */
    'aria-controls': string | undefined
    /** Whether the listbox popup is currently expanded */
    'aria-expanded': boolean
    /** Type of popup that can be triggered */
    'aria-haspopup': 'listbox'
    /** Type of autocomplete behavior */
    'aria-autocomplete': 'list'
    /** ID of the currently active descendant option */
    'aria-activedescendant': string | undefined
    /** Accessible label for the input */
    'aria-label': string | undefined
    /** IDs of elements that describe the input */
    'aria-describedby': string | undefined
    /** ARIA role for the input element */
    role: 'combobox'
  }
}

/**
 * Manages WCAG-compliant accessibility attributes and announcements for SmartTimeInput
 *
 * This hook implements the ARIA 1.2 combobox pattern with listbox popup to ensure
 * the time input is fully accessible to screen reader users and keyboard navigation.
 * It handles dynamic announcements, proper ARIA relationships, and state management
 * for assistive technologies.
 *
 * Key accessibility features:
 * - **ARIA combobox pattern**: Implements proper combobox with listbox popup
 * - **Live region announcements**: Announces suggestion count and active selection
 * - **Navigation hints**: Provides one-time hint about keyboard controls
 * - **Error association**: Properly links validation errors via aria-errormessage
 * - **Active descendant**: Tracks and announces the focused suggestion
 * - **Dynamic updates**: Manages ARIA attributes based on dropdown state
 *
 * @param props - Configuration object for accessibility management
 * @param props.isOpen - Whether the suggestion dropdown is currently open
 * @param props.activeIndex - The currently highlighted suggestion index (-1 if none)
 * @param props.suggestions - Array of available time suggestions
 * @param props.suggestionCount - Total count of suggestions available
 * @param props.hasError - Whether the input has a validation error
 * @param props.errorId - ID of the error message element for aria-errormessage
 * @param props.id - Optional ID for the input element
 * @param props.name - Optional name for the input element
 * @param props.ariaLabel - Optional accessible label for the input
 * @param props.ariaDescribedBy - Optional IDs of elements that describe the input
 *
 * @returns Object containing listbox ID, live region message, and input ARIA props
 *
 * @example
 * ```tsx
 * const { listboxId, liveRegionMessage, inputAriaProps } = useComboboxAccessibility({
 *   isOpen: dropdownOpen,
 *   activeIndex: highlightedIndex,
 *   suggestions: timeSuggestions,
 *   suggestionCount: timeSuggestions.length,
 *   hasError: !!error,
 *   errorId: error ? `${inputId}-error` : undefined,
 *   ariaLabel: 'Schedule time',
 * })
 *
 * return (
 *   <>
 *     <input {...inputAriaProps} />
 *     <div role="listbox" id={listboxId}>...</div>
 *     <div role="status" aria-live="polite">{liveRegionMessage}</div>
 *   </>
 * )
 * ```
 */
export const useComboboxAccessibility = ({
  isOpen,
  activeIndex,
  suggestions,
  suggestionCount,
  hasError,
  errorId,
  ariaLabel,
  ariaDescribedBy,
}: UseComboboxAccessibilityProps): ComboboxAccessibilityResult => {
  const listboxId = useId()
  const [hasAnnouncedHint, setHasAnnouncedHint] = useState(false)
  const previousMessageRef = useRef<string>('')

  const activeSuggestion = useMemo(
    () => (activeIndex >= 0 ? suggestions[activeIndex] : null),
    [activeIndex, suggestions],
  )

  const liveRegionMessage = useMemo(() => {
    if (!isOpen) {
      return ''
    }

    if (activeSuggestion) {
      const message = `${activeSuggestion.displayText}, ${
        activeIndex + 1
      } of ${suggestionCount}`
      const hint = !hasAnnouncedHint
        ? '. Use arrow keys to navigate, Enter to select'
        : ''
      return message + hint
    }

    return `${suggestionCount} suggestion${
      suggestionCount === 1 ? '' : 's'
    } available`
  }, [isOpen, activeSuggestion, activeIndex, suggestionCount, hasAnnouncedHint])

  // Track when hint has been announced by checking if message changed
  useEffect(() => {
    if (
      liveRegionMessage &&
      liveRegionMessage !== previousMessageRef.current &&
      !hasAnnouncedHint &&
      activeSuggestion &&
      liveRegionMessage.includes('Use arrow keys to navigate')
    ) {
      previousMessageRef.current = liveRegionMessage
      // Delay setting hasAnnouncedHint to ensure screen reader announces the hint
      const timeoutId = setTimeout(() => {
        setHasAnnouncedHint(true)
      }, 100)
      return () => clearTimeout(timeoutId)
    }
    previousMessageRef.current = liveRegionMessage
  }, [liveRegionMessage, hasAnnouncedHint, activeSuggestion])

  // Reset hint when menu closes
  useEffect(() => {
    if (!isOpen) {
      setHasAnnouncedHint(false)
      previousMessageRef.current = ''
    }
  }, [isOpen])

  const inputAriaProps = useMemo(
    () => ({
      'aria-invalid': hasError || undefined,
      'aria-errormessage': hasError && errorId ? errorId : undefined,
      'aria-controls': isOpen ? listboxId : undefined,
      'aria-expanded': isOpen,
      'aria-haspopup': 'listbox' as const,
      'aria-autocomplete': 'list' as const,
      'aria-activedescendant': activeSuggestion
        ? `${listboxId}-option-${activeIndex}`
        : undefined,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      role: 'combobox' as const,
    }),
    [
      hasError,
      errorId,
      isOpen,
      listboxId,
      activeSuggestion,
      activeIndex,
      ariaLabel,
      ariaDescribedBy,
    ],
  )

  return {
    listboxId,
    liveRegionMessage,
    inputAriaProps,
  }
}
